// Small shared pieces (02-building-blocks.md): cards, status labels, badges, typing dots, avatars,
// the empty state.
import { useEffect, useLayoutEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { SESSION_SENT } from '../core/worker';
import { badgeCount, idColour, initial } from '../core/format';
import { frameIcons } from '../frame/icons';
import { Icon } from './icons';

export function NoticeCard(props: { tone?: 'primary' | 'critical'; title: ReactNode; description?: ReactNode; after?: ReactNode }) {
  return (
    <div className={`notice notice-${props.tone ?? 'primary'}`}>
      <div className="notice-row">
        <div className="notice-text">
          <p className="notice-title">{props.title}</p>
          {props.description && <p className="notice-description">{props.description}</p>}
        </div>
        {props.after}
      </div>
    </div>
  );
}

/** Neutral for states, problem (red) only for a problem. */
export const StatusLabel = ({ tone = 'neutral', children }: { tone?: 'neutral' | 'problem'; children: ReactNode }) => (
  <span className={`status-label status-${tone}`}>{children}</span>
);

// Badges don't move while a start or a switch catches up with the server (12, Living badges).
let catchingUp = 0;
export const stillWhile = (caughtUp: Promise<unknown>) => void (catchingUp++, caughtUp.finally(() => setTimeout(() => catchingUp--, 100)));

/** Motion is on: the browser animates and the system doesn't ask for less (01, Motion). */
const moving = () => !catchingUp && !!document.body.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A count's changes within 500 ms are taken together, to the newest (12, Living badges); to or from none at once. */
export function useTogether(n: number): number {
  const [shown, setShown] = useState(n);
  const latest = useRef(n);
  latest.current = n;
  const waiting = n !== shown && !!n && !!shown;
  if (n !== shown && !waiting) setShown(n);
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setShown(latest.current), 500);
    return () => clearTimeout(timer);
  }, [waiting, shown]);
  return shown;
}

/**
 * A number that changes in place (12, Living badges): the old one slides up and fades while the new
 * one comes from below (160 ms, ease-out) and the width follows (120 ms); never on the first draw,
 * at once with reduced motion.
 */
export function Rolling({ text }: { text: string }) {
  const [drawn, setDrawn] = useState(text);
  const [old, setOld] = useState<string>();
  if (drawn !== text) (setDrawn(text), setOld(moving() ? drawn : undefined));
  const box = useRef<HTMLSpanElement>(null);
  const width = useRef(0);
  useLayoutEffect(() => {
    const el = box.current!;
    const from = width.current;
    width.current = el.offsetWidth;
    if (old === undefined) return;
    const slide = { duration: 160, easing: 'ease-out' };
    const runs = [
      el.firstElementChild!.animate({ transform: ['none', 'translateY(-100%)'], opacity: [1, 0] }, { ...slide, fill: 'forwards' }),
      el.lastElementChild!.animate({ transform: ['translateY(100%)', 'none'], opacity: [0, 1] }, slide),
      el.animate({ width: [`${from || width.current}px`, `${width.current}px`] }, 120),
    ];
    runs[0].onfinish = () => setOld(undefined);
    return () => runs.forEach((a) => a.cancel());
  }, [old, text]);
  return (
    <span className="rolling" ref={box}>
      {old !== undefined && <span aria-hidden="true">{old}</span>}
      <span>{text}</span>
    </span>
  );
}

/**
 * A badge or dot that comes and goes (12, Living badges): it grows from 60 % with a slight
 * overshoot (200 ms) and shrinks to 60 % fading (120 ms), drawn with its last value while it goes;
 * never on the first draw, at once with reduced motion.
 */
function useLiving<T>(value: T | undefined) {
  const on = value !== undefined;
  const [was, setWas] = useState(on);
  const [change, setChange] = useState(false);
  if (was !== on) (setWas(on), setChange(moving()));
  const last = useRef(value);
  if (on) last.current = value;
  const el = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!change || !el.current) return;
    const run = on
      ? el.current.animate({ transform: ['scale(0.6)', 'none'] }, { duration: 200, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' })
      : el.current.animate({ transform: ['none', 'scale(0.6)'], opacity: [1, 0] }, { duration: 120, fill: 'forwards' });
    run.onfinish = () => setChange(false);
    return () => run.cancel();
  }, [change, on]);
  return [on || change ? last.current : undefined, el] as const;
}

/** The unread badge in lists: the count, always red (the colour says nothing of mentions), or a grey dot; living (12). */
export function UnreadBadge({ count = 0, on = true }: { count?: number; on?: boolean }) {
  const [n, el] = useLiving(on ? count : undefined);
  const text = badgeCount(useTogether(n ?? 0));
  if (n === undefined) return null;
  return (
    <span ref={el} className={n ? 'unread red' : 'unread dot'} data-desyr-unread={n ? text : undefined}>
      {n ? <Rolling text={text} /> : null}
    </span>
  );
}

/** The rail's dot: dark for unread, red when something is for the person; it comes and goes as the badges (12). */
export function RailDot({ dot }: { dot?: 'dot' | 'red' }) {
  const [shown, el] = useLiving(dot);
  return shown ? <span ref={el} className={shown === 'red' ? 'rail-dot red' : 'rail-dot'} data-desyr-rail-badge={shown === 'red' ? 'red dot' : 'dot'} /> : null;
}

export const TypingDots = () => (
  <span className="typing" aria-hidden="true">
    <i />
    <i />
    <i />
  </span>
);

export interface AvatarProps {
  /** The ID that picks the colour (a user or room ID); never shown. */
  id: string;
  name?: string;
  src?: string;
  size?: number;
  /** People are round; channels and spaces keep rounded corners. */
  round?: boolean;
  /** Spaces on the rail show two initials. */
  letters?: number;
  /** The picture's alt text where it stands for something (the rail's spaces, 90 §2). */
  alt?: string;
}

/** A load this quick came from the browser's cache: it shows at once, without fading (01, Motion). */
const QUICK = 50;

/**
 * A picture of the interface (02, "Pictures that don't load"): drawn only once it has loaded, never
 * the browser's broken-image icon; meanwhile and after a failure its `fallback`. A failure is tried
 * once more after about 2 s, and again whenever the page sends its session to the worker. With `fade`
 * (01, Motion, v1.58.24) it fades in over its fallback in 150 ms, kept until then; one already in the
 * browser's memory or cache is there on the first frame, and with reduced motion it shows at once.
 */
export function Picture({ src, fallback = null, onShown, fade, ...img }: ImgHTMLAttributes<HTMLImageElement> & { fallback?: ReactNode; onShown?: (shown: boolean) => void; fade?: boolean }) {
  const [state, setState] = useState<'loading' | 'fading' | 'shown' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  const picture = useRef<HTMLImageElement>(null);
  const since = useRef(0);
  useLayoutEffect(() => {
    since.current = performance.now();
    setState(picture.current?.complete && picture.current.naturalWidth ? 'shown' : 'loading');
  }, [src, attempt]);
  useEffect(() => void onShown?.(state === 'shown'), [state]);
  useEffect(() => {
    if (state === 'fading') {
      const timer = setTimeout(() => setState('shown'), 150);
      return () => clearTimeout(timer);
    }
    if (state !== 'failed') return;
    const again = () => (setAttempt((a) => a + 1), setState('loading'));
    const timer = attempt ? undefined : setTimeout(again, 2000);
    addEventListener(SESSION_SENT, again);
    return () => (clearTimeout(timer), removeEventListener(SESSION_SENT, again));
  }, [state, attempt]);
  const loaded = () => {
    const still = !fade || performance.now() - since.current < QUICK || matchMedia('(prefers-reduced-motion: reduce)').matches;
    setState(still ? 'shown' : 'fading');
  };
  return (
    <>
      {src && state !== 'failed' && (
        <img
          key={attempt}
          ref={picture}
          {...img}
          src={src}
          className={state === 'fading' ? `${img.className ? `${img.className} ` : ''}fade-in` : img.className}
          style={state === 'loading' ? { display: 'none' } : img.style}
          onLoad={loaded}
          onError={() => setState('failed')}
        />
      )}
      {(!src || state !== 'shown') && fallback}
    </>
  );
}

export function Avatar({ id, name, src, size = 32, round = true, letters = 1, alt }: AvatarProps) {
  const [loaded, setLoaded] = useState(false);
  // The coloured initials from the first frame, under the picture until it has faded in (02, Avatars).
  return (
    <span
      className={round ? 'avatar round' : 'avatar'}
      style={{ width: size, height: size, fontSize: size * 0.45, background: loaded ? 'transparent' : `var(--name-${idColour(id)})` }}
      aria-hidden={alt ? undefined : true}
    >
      <Picture src={src} alt={alt ?? ''} onShown={setLoaded} fallback={initial(name, letters)} fade />
    </span>
  );
}

/** The branded ones that have faded in on this page: each fades in only once (02, v1.58.26). */
const faded = new Set<string>();
// The rail's bubble without its three dots (02, v1.58.27): the path's first part, the body and the tail.
const bubble = `url("data:image/svg+xml,${encodeURIComponent(frameIcons.bubble.replace(/ZM[^"]*/, 'Z'))}")`;

/** The empty state (02); with a place, the branded one: desyr's bubble in the brand gradient, the place's symbol on it. */
export function EmptyState({ icon, title, text, action, place, once = place }: { icon: string; title?: string; text: string; action?: ReactNode; place?: 'room' | 'search' | 'files'; once?: string }) {
  const [fade] = useState(() => !!once && !faded.has(once));
  useEffect(() => void (once && faded.add(once)), [once]);
  return (
    <div className={place ? `empty branded${fade ? ' fade' : ''}` : 'empty'} data-desyr-empty={place}>
      <span className={place ? 'empty-bubble' : 'empty-icon'}>
        {place && <i style={{ maskImage: bubble, WebkitMaskImage: bubble }} />}
        <Icon svg={icon} size={place ? 40 : 36} />
      </span>
      {title && <h5>{title}</h5>}
      <p>{text}</p>
      {action}
    </div>
  );
}
